import { useEffect, useRef } from "react";
import type { PollResponse } from "../options";

const POLLING_INTERVAL_MS = 5000;

type UseUploadPollingOptions = {
  fileId: string | null;
  isProcessing: boolean;
  onPoll: ((fileId: string) => Promise<PollResponse>) | undefined;
  onSuccess: (stats: PollResponse) => void;
  onError: (error: Error) => void;
};

/**
 * Hook that handles polling for upload processing status.
 * Polls at regular intervals while in PROCESSING state until
 * lastMatched is populated (indicating completion).
 */
export function useUploadPolling({
  fileId,
  isProcessing,
  onPoll,
  onSuccess,
  onError,
}: UseUploadPollingOptions): void {
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isProcessing || !fileId || !onPoll) {
      return;
    }

    const poll = async () => {
      try {
        const stats = await onPoll(fileId);
        if (stats.lastMatched) {
          onSuccess(stats);
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      } catch (error) {
        onError(error instanceof Error ? error : new Error("Polling failed"));
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    };

    // Initial poll
    poll();

    // Start interval
    pollingRef.current = setInterval(poll, POLLING_INTERVAL_MS);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [fileId, isProcessing, onPoll, onSuccess, onError]);
}
